// Import required dependencies
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { i18n } from '@/locale/i18n'
import MarketListHeader from '@/features/market/market-list-header'
import FilterDropdownPanel from '@/components/filter-dropdown-panel'
import InfiniteGrid from '@/layouts/infinite-grid'
import constants from '@/constants'
import Modal from '@/layouts/modal/Modal'

// Commented out unused import
// import Market from 'questions/components/market'

// Import styles and components
import './MarketListTemplate.scss'
import MarketCard from '../market-card/MarketCard'
import { MarketListCardSkeleton, MarketListRowSkeleton, MarketListFixtureRowSkeleton } from './MarketListCardSkeleton'
import MarketListColumnHeaders from './MarketListColumnHeaders'
import { ArrowUpDown, ArrowStandIcon, ViewGrid, ViewList } from '@/assets/icons'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import RadioControl from '@/components/ui/RadioControl'
import SegmentedToggle from '@/components/ui/segmented-toggle'
import { leadingRowOutcomeFor, priceCurrencyIdFor } from '@/features/market/outcome-list/outcomePricing'
import { isClickable } from '@/features/market/outcome-item/utils/outcomeState'
import { isSportsEvent } from '@/features/market/utils/sportsEvent'
import { useActiveCurrency } from '@/hooks'
import ActionSheetLayout from '@/components/action-sheet-layout'
import EmptyState from '@/components/ui/EmptyState'
import { urlSearchParams } from '@/util/urlSearchParams'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setOutcomeSelected } from '@/store/app'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import useElementWidth from '@/hooks/useElementWidth'
import { marketsUrl } from '@/urls'
import {
  parseMarketView,
  readStoredMarketView,
  storeMarketView,
  MARKET_VIEW,
  LIST_VIEW_KIND,
} from '@/constants/marketView'

/** The list view is a single full-width column at every size. */
const LIST_VIEW_BREAKPOINTS = { default: 1 }

/** "Closing soon" — kickoff order for fixtures, which is how a match list is read. */
const CHRONOLOGICAL_ORDERING = 'bet_end_date'

/* Whether the bet pad can dock beside the list is a question about room, not
   about the viewport: collapsing the categories column hands the list ~260px,
   which on many screens is the difference between a docked column and a sheet.
   So the template measures the band it was given and works backwards. */

/** Narrowest the rows may get before the docked column has to give way. */
const LIST_MIN_WIDTH = 950
/** Matches the `w-[340px]` column — PlaceBet's own width — and the `gap-5` beside it. */
const BET_COLUMN_WIDTH = 340
const BET_COLUMN_GAP = 20
/** `px-4 md:px-[30px]` on the band; the list view is desktop-only, so md wins. */
const LIST_AREA_GUTTERS = 60
const BET_COLUMN_MIN_AREA = LIST_MIN_WIDTH + BET_COLUMN_GAP + BET_COLUMN_WIDTH + LIST_AREA_GUTTERS

/** Long enough to register as a reload, short enough not to read as a wait. */
const VIEW_SWAP_BLINK_MS = 220

/**
 * MarketListTemplate Component
 * Renders a list of markets with filtering, sorting and infinite scroll capabilities
 *
 * @param {string} activeMarketType - Type of market (real money vs play money)
 * @param {object} queryParams - URL query parameters for filtering
 * @param {array} markets - Array of market data
 * @param {object} pagination - Pagination information
 * @param {string} status - Current loading status
 * @param {object} error - Error object if any
 * @param {function} onLoadMore - Callback for loading more markets
 * @param {function} setMarketSelected - Callback to select a market
 * @param {object} marketSelected - Currently selected market
 * @param {string} page - Current page identifier
 * @param {boolean} autoLoadMore - Load the next page on scroll instead of showing a CTA (defaults to every page but `feed`)
 * @param {number} hiddenRowsCount - Markets counted by `pagination.total` that the host renders outside this grid
 * @param {boolean} marketsSettled - False while the host is still deciding which markets it pulls out of the grid
 * @param {string} listKey - Names the list the host is showing (category, chip, tag…); a new value means new rows
 * @param {object} breakpoints - Breakpoints for responsive layout
 * @param {function} onBreakpointChange - Callback when breakpoint changes
 */
const MarketListTemplate = ({
  activeMarketType,
  queryParams,
  markets,
  pagination,
  status,
  error,
  onLoadMore,
  setMarketSelected = () => {},
  marketSelected,
  page = 'feed',
  autoLoadMore,
  hiddenRowsCount = 0,
  /** A host that promotes markets out of the grid (home's highlight strip) is still
      deciding which ones until its own request lands; until then the rows on screen are
      not the rows that will stay, and nothing that reads "the first row" should run. */
  marketsSettled = true,
  /** Whatever picks the rows besides the URL — a category chip held in the host's own
      state, say. The template cannot see those, and the selection has to start over
      whenever they change, exactly as it does on a new URL. */
  listKey: hostListKey = '',
  breakpoints = { default: 4, 1440: 4, 1439: 3, 1024: 2, 981: 1 },
  onBreakpointChange = () => {},
  onOpenSideMenu,
  showSideMenuToggle = false,
  showMobileSideMenuToggle = false,
  sideMenuOpen = false,
  categoryTitle = '',
  titleAction = null,
  /** Replaces the grid's default "no markets" text when the list comes back empty. */
  emptyState = null,
  headerClassName = '',
  gridInsert = null,
  subheader = null,
  /** Offers the card/list switch. On by default: the choice is stored and follows the
      reader from page to page, which only holds if every list offers it. A page whose
      rows would make no sense opts out. */
  enableListView = true,
  /** Fixture rows read by kickoff and need match data; everything else is a market
      row of title plus odds. Drives the default ordering and the loading frame. */
  listViewKind = LIST_VIEW_KIND.MARKETS,
}) => {
  // State management
  const [sort, setSort] = useState()
  /* Held in state, not a ref: the cards need a re-render once the column mounts,
     otherwise the first selection has nowhere to portal its bet pad. */
  const [betColumn, setBetColumn] = useState(null)
  /* Border box, so the measurement does not shift when the band takes on its own
     padding in column mode — that would let the two layouts flip each other. */
  const [listAreaWidth, listAreaRef] = useElementWidth()
  const [sortModalOpen, setSortModalOpen] = useState(false)
  // Add state to track which market has its bet list open
  const [openBetListMarketId, setOpenBetListMarketId] = useState(null)
  const [safariInitialLoadTriggered, setSafariInitialLoadTriggered] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const isMobileSize = useIsMobileSize()
  const dispatch = useDispatch()
  const activeCurrency = useActiveCurrency()
  const params = new urlSearchParams(location.search || '')

  /* The row trades horizontal room for density, which a phone does not have, so
     the list is desktop only; `listView` follows the toggle, which keeps a list
     chosen on a desktop (or a window dragged narrow) on cards on a phone. */
  const showViewToggle = enableListView && !isMobileSize
  const isFixtureList = listViewKind === LIST_VIEW_KIND.FIXTURES
  /* The choice lives in storage so it follows the reader from page to page and across
     reloads. The feed routes are server-rendered and hydrated, and the server cannot see
     storage, so the first render agrees with the server (cards) and the stored choice is
     applied from a mount effect: one card paint, then the layout the reader chose. */
  const [marketView, setStoredMarketView] = useState(MARKET_VIEW.CARD)
  useEffect(() => {
    setStoredMarketView(readStoredMarketView())
  }, [])
  const listView = showViewToggle && marketView === MARKET_VIEW.LIST

  /* Unmeasured (SSR, or a browser without ResizeObserver) reads as "no room":
     the sheet works at every width, the docked column does not. */
  const fitsBetColumn = listAreaWidth !== null && listAreaWidth >= BET_COLUMN_MIN_AREA
  const showBetColumn = listView && fitsBetColumn
  /* Without the room for both, the pad rises from the bottom-right as a sheet
     instead of pushing the list around, so the rows keep their width. */
  const showBetSheet = listView && !fitsBetColumn

  /* Switching layout rebuilds every row, which auto-animate would otherwise slide into
     place one card at a time. The rows are blanked to skeletons for a beat instead, so the
     swap reads as a list reloading rather than as the old one rearranging itself. */
  const [swappingView, setSwappingView] = useState(false)
  const swapTimeout = useRef(null)
  useEffect(() => () => clearTimeout(swapTimeout.current), [])

  const setMarketView = view => {
    if (parseMarketView(view) !== marketView) {
      setSwappingView(true)
      clearTimeout(swapTimeout.current)
      swapTimeout.current = setTimeout(() => setSwappingView(false), VIEW_SWAP_BLINK_MS)
    }
    storeMarketView(view)
    setStoredMarketView(parseMarketView(view))
    if (view !== MARKET_VIEW.LIST) setMarketSelected(null)
  }

  /* A list of fixtures is read by kickoff time, so it opens in date order — unless the
     user already picked an ordering, which stays theirs. A list of markets keeps whatever
     the feed was already ranked by. An effect rather than part of the toggle: the list
     now follows the reader onto a fixture page they did not toggle on. */
  useEffect(() => {
    if (!listView || !isFixtureList) return
    const next = new URLSearchParams(Object.fromEntries(new urlSearchParams(location.search || '')))
    if (next.get('ordering')) return
    next.set('ordering', CHRONOLOGICAL_ORDERING)
    navigate(`${location.pathname}?${next.toString()}`, { replace: true })
  }, [listView, isFixtureList, location.pathname, location.search, navigate])
  const isLoading = status === constants.LOADING || status === constants.IDLE
  /* Skeletons stand in while the feed is loading its first rows, and for the blink that
     covers a layout swap — where the rows are already in hand but every one is rebuilt.
     The blink keeps one skeleton per loaded market, so the page holds its height and
     scroll position and the grid does not read the shorter page as a call for more. */
  const showInitialSkeleton = isLoading && markets.length === 0
  const showSkeletonRows = swappingView || showInitialSkeleton
  const loadsOnScroll = autoLoadMore ?? page !== 'feed'

  /* A new list — another chip, another category, another route — drops the market and
     the outcome left over from the previous one: both outlive the list they came from,
     and a row shows its pad only for the selection it owns, so a stale pair would leave
     the column pointing at a market that is not on the page. */
  const listKey = `${location.pathname}${location.search}|${hostListKey}`
  const [preparedList, setPreparedList] = useState(null)
  useEffect(() => {
    if (!listView || preparedList === listKey) return
    setMarketSelected(null)
    dispatch(setOutcomeSelected(null))
    setPreparedList(listKey)
  }, [listView, listKey, preparedList, dispatch])

  /* The column opens on the first row rather than standing empty until the reader picks
     one. Not simply the first: a list ordered by date leads with whatever closes soonest,
     which is often closed or resolved already, so it is the first row whose leading
     outcome can take a bet — the same outcome the row itself lights up. A list with no
     such row (the closed filter, say) opens on its first row anyway: the pad then shows
     that market's state, as a click on the row would. */
  const seedRow = useMemo(() => {
    if (!showBetColumn) return null
    let first = null
    for (const market of markets) {
      /* Asked of the market, not of the list: a fixture can turn up in a markets-kind
         list, and the card reads its own kind the same way. */
      const outcome = leadingRowOutcomeFor(market, activeCurrency?.id, { isFixture: isSportsEvent(market) })
      if (!outcome) continue
      first ??= { market, outcome }
      if (isClickable(outcome, market, null, priceCurrencyIdFor(market, activeCurrency?.id))) {
        return { market, outcome }
      }
    }
    return first
  }, [showBetColumn, markets, activeCurrency?.id])

  /* The selected market can also go missing without the key changing — the store is
     reset under the list, or the rows are refetched for a reason the template cannot see.
     Once the rows have settled, a selection none of them owns is dropped, and the seed
     below starts the list over on its first row. */
  const selectedMarketPresent = markets.some(market => market?.id === marketSelected?.id)
  useEffect(() => {
    if (!listView || isLoading || !marketsSettled || !marketSelected || selectedMarketPresent) return
    setMarketSelected(null)
    dispatch(setOutcomeSelected(null))
  }, [listView, isLoading, marketsSettled, marketSelected, selectedMarketPresent, dispatch])

  /* Runs once the list above has been cleared, the fetch has settled and the host has
     settled which rows stay, and only while no market is picked, so it never takes a
     selection from the reader: a row that picks also names its market, and the store's
     outcome was cleared with it above. The row that owns the pair opens its pad in the
     column by itself. */
  useEffect(() => {
    if (!showBetColumn || isLoading || !marketsSettled || preparedList !== listKey) return
    if (marketSelected || !seedRow) return
    setMarketSelected(seedRow.market)
    dispatch(setOutcomeSelected(seedRow.outcome))
  }, [showBetColumn, isLoading, marketsSettled, preparedList, listKey, marketSelected, seedRow, dispatch])

  // Set prerender flag when markets are loaded
  useEffect(() => {
    if (!isLoading && markets.length > 0) {
      setTimeout(() => {
        window.prerenderReady = true
      }, 1000)
    }
  }, [isLoading, markets])

  // Safari-specific fix: Ensure initial markets load when component mounts
  useEffect(() => {
    if (!safariInitialLoadTriggered && markets.length === 0 && !isLoading && pagination?.total > 0) {
      // Safari sometimes doesn't trigger scroll detection properly on initial load
      // Force a load after a small delay to ensure proper rendering
      const timer = setTimeout(() => {
        onLoadMore(activeMarketType, { startIndex: 0 })
        setSafariInitialLoadTriggered(true)
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [safariInitialLoadTriggered, markets.length, isLoading, pagination?.total, activeMarketType, onLoadMore])

  /**
   * Handles toggling the bet list for a market
   * @param {string} marketId - ID of the market to toggle
   * @param {boolean} isCollapsed - Current collapse state
   */
  /* Picking another fixture — by its row or by one of its odds — folds the open
     bet list back up, so the expanded row always matches the selected market. */
  const selectedMarketId = marketSelected?.id
  useEffect(() => {
    if (!listView || selectedMarketId == null) return
    setOpenBetListMarketId(openId => (openId !== null && openId !== selectedMarketId ? null : openId))
  }, [listView, selectedMarketId])

  const handleToggleBetList = (marketId, isCollapsed) => {
    // If the bet list is being opened, set this market as the open one
    // If it's being closed, clear the open market ID
    setOpenBetListMarketId(isCollapsed ? null : marketId)
  }

  /**
   * Renders individual market card
   * @param {object} param0 - Contains index of the market to render
   */
  const onRender = ({ index }) => {
    const market = markets[index]
    if (!market) return

    return (
      <MarketCard
        key={'question-card-' + market.id}
        question={market}
        categories={market && market.category}
        inlineBehavior
        showBetStatus
        showSelectedOutcome
        isSelectedToBet={market && marketSelected && marketSelected.id === market.id}
        setMarketSelected={setMarketSelected}
        // Pass the forced collapse state based on whether this market is the one with open bet list
        forceBetListCollapsed={openBetListMarketId !== null && openBetListMarketId !== market.id}
        /* Rows are controlled outright: one open id, so one expanded row. */
        betListOpen={listView ? openBetListMarketId === market.id : undefined}
        // Pass the handler for toggling bet list
        onToggleBetList={isCollapsed => handleToggleBetList(market.id, isCollapsed)}
        inMarketPage={false}
        // First row (~4 cards): prioritize LCP thumbnail decode with SSR image preload.
        prioritizeLcpImage={index < 4}
        compactRow={listView}
        quickBetPortalTarget={betColumn}
        quickBetAsSheet={showBetSheet}
      />
    )
  }

  /**
   * Renders skeleton loading cards (same as feed loading), or row-shaped
   * skeletons when the list is showing fixtures.
   */
  const onRenderSegments = ({ index }) => {
    if (!listView) return <MarketListCardSkeleton index={index} />
    return isFixtureList ? <MarketListFixtureRowSkeleton /> : <MarketListRowSkeleton />
  }

  /** Card/list switch, offered by every list so the stored choice holds everywhere. */
  const renderViewToggle = () => {
    if (!showViewToggle) return null

    return (
      <SegmentedToggle
        /* 48px: the filter panel overrides its dropdowns to that height, and the
           toggle shares their row. `mt-1` matches the panel's own top padding so
           the two line up on the same edge, not just on their centres. */
        size="large"
        className="mt-1"
        ariaLabel={i18n('markets.view_mode')}
        value={listView ? MARKET_VIEW.LIST : MARKET_VIEW.CARD}
        onChange={setMarketView}
        options={[
          { value: MARKET_VIEW.CARD, Icon: ViewGrid, label: i18n('markets.view_cards') },
          { value: MARKET_VIEW.LIST, Icon: ViewList, label: i18n('markets.view_list') },
        ]}
      />
    )
  }

  const renderSortDropdown = () => {
    const hideModal = () => {
      setSortModalOpen(false)
    }
    const renderList = () => {
      const sortByOptions = [
        { value: '-created_on', text: i18n('markets.most_recent') },
        { value: 'bet_end_date', text: i18n('markets.bet_end_date') },
        { value: '-wagers_count', text: i18n('markets.number_of_bets') },
        { value: '-volume', text: i18n('markets.volume') },
        { value: '-relevance', text: i18n('markets.relevance') },
      ]
      return (
        <ul>
          {sortByOptions.map((header, index) => (
            <li
              key={'sort-portfolio-dropdown' + index}
              className="flex gap-2 px-2 py-3"
              onClick={() => {
                // Update URL with new sort parameter
                const _queryParams = Object.fromEntries(new urlSearchParams(location.search))
                _queryParams.ordering = header.value
                setSort(header.value)
                navigate(location.pathname + '?' + new URLSearchParams(_queryParams).toString())
                hideModal()
              }}>
              <RadioControl active={sort === header.value} />
              <div className="flex items-center gap-2 cursor-pointer">{header.text}</div>
            </li>
          ))}
        </ul>
      )
    }
    return (
      <>
        <div
          onClick={() => setSortModalOpen(true)}
          className="rounded-lg flex w-10 h-10 items-center justify-center bg-surface-2 border border-solid border-Neutral-3">
          <ArrowUpDown width="13px" fill="currentColor" />
        </div>

        <Modal
          isOpen={sortModalOpen}
          position="bottom"
          onCloseRequested={hideModal}
          title={i18n('markets.sort_by')}
          content={renderList()}
          onClose={hideModal}
          showCloseButton></Modal>
      </>
    )
  }

  return (
    <React.Fragment>
      <div>
        <MarketListHeader
          isLoading={false}
          countLoading={isLoading}
          numberOfActiveBets={!pagination?.total ? null : pagination.total}
          isRealMoney={activeMarketType === constants.REAL_MONEY}
          className={headerClassName}
          title={categoryTitle || params.get('search')}
          titleAction={titleAction}
          leftContent={
            showSideMenuToggle && onOpenSideMenu ? (
              <ButtonIcon
                size="medium"
                highlightOnHover
                cursorPointer
                handleOnClick={onOpenSideMenu}
                ariaLabel={i18n('filters.toggle_categories')}
              >
                <ArrowStandIcon
                  width="16"
                  height="16"
                  style={sideMenuOpen ? { transform: 'scaleX(-1)' } : undefined}
                />
              </ButtonIcon>
            ) : null
          }
          onTitleClick={showMobileSideMenuToggle && onOpenSideMenu ? onOpenSideMenu : undefined}
          filters={
            <div className="flex justify-between gap-2 w-full items-center">
              {isMobileSize ? (
                <FilterDropdownPanel
                  className="market-page__filters  md:flex-wrap max-h-[calc(100vh-100px)] md:max-h-none pb-32 md:pb-0 items-start md:items-center"
                  queryParams={queryParams}
                  page={page}
                  containerClass="!p-0  "
                />
              ) : (
                <div className="flex items-center gap-2">
                  <FilterDropdownPanel
                    className="market-page__filters  md:flex-wrap items-start md:items-center"
                    queryParams={queryParams}
                    page={page}
                    containerClass="!p-0  "
                  />
                </div>
              )}
              <div className="flex shrink-0 items-center gap-2">
                {renderViewToggle()}
                {isMobileSize && renderSortDropdown()}
              </div>
            </div>
          }
        />
      </div>

      {subheader}

      {/* List view runs the rows beside a docked bet column; the grid keeps the
          full width to itself. */}
      {/* Same 30px the heading and the chip strip start at, so the panel lines up with them
          whether or not the bet column is docked beside it. */}
      <div ref={listAreaRef} className={showBetColumn ? 'flex items-start gap-5 px-4 md:px-[30px]' : undefined}>
        <div className={showBetColumn ? 'min-w-0 flex-1' : undefined}>
          {/* Column names sit outside the grid so they stay put while rows stream in;
              an empty list has no columns to name. */}
          {/* The gutter rides on the header itself, so it lines up with the grid's. */}
          {listView && (markets.length > 0 || isLoading) && (
            <MarketListColumnHeaders
              listViewKind={listViewKind}
              className={showBetColumn ? undefined : 'mx-4 md:mx-[30px]'}
            />
          )}
          {/* Infinite scrolling grid of market cards */}
          <InfiniteGrid
            /* The grid's rows start at 18px plus the masonry gutter's 12, which is where the
               heading and the chip strip sit. The list's single column is the panel itself
               and carries a border, so a gutter there would fall inside it — the wrapper
               makes up the same 30px instead. */
            className={`market-page__wrapper !mb-0 pb-20 md:pb-16 ${
              showBetColumn ? '!px-0' : listView ? '!px-4 md:!px-[30px]' : '!px-4 md:!px-[18px]'
            }`}
            /* Masonry columns size to their content; the single list column has to be
               told to fill the row instead. */
            columnClassName={`market-page__column ${listView ? 'market-page__column--list' : 'space-y-6'}`}
            error={error}
            isLoading={isLoading}
            loadedRows={
              showInitialSkeleton
                ? breakpoints[
                    typeof window !== 'undefined'
                      ? Object.keys(breakpoints).find(bp => window.innerWidth <= bp) || 'default'
                      : 'default'
                  ] * 4
                : markets.length
            }
            onLoadMore={options => onLoadMore(activeMarketType, options)}
            animateRows={!swappingView}
            onRowRender={showSkeletonRows ? onRenderSegments : onRender}
            placeholder={
              /* Sits a screenful down the grid rather than against the filters, so an empty
                 result reads as the answer to them instead of a caption under them. The
                 offset lives here so every empty state the grid can show starts at the same
                 height; the grid's own 20px margin is taken off it. */
              <div className="pt-[120px]">
                {emptyState || (
                  <EmptyState
                    className="!py-0"
                    title={i18n('markets.no_markets_title')}
                    description={Object.keys(queryParams).length > 0 ? i18n('markets.no_markets_filters_hint') : ''}
                  />
                )}
              </div>
            }
            totalRows={pagination ? Math.max(pagination.total - hiddenRowsCount, 0) : 0}
            useMasonry
            autoLoading={loadsOnScroll}
            autoLoadPaused={swappingView}
            /* Rows span the full width; the masonry columns collapse to one. */
            breakpoints={listView ? LIST_VIEW_BREAKPOINTS : breakpoints}
            onBreakpointChange={onBreakpointChange}
            onViewAllClick={
              !loadsOnScroll && page === 'feed'
                ? () => {
                    window.scrollTo({ top: 0, behavior: 'instant' })
                    navigate(marketsUrl())
                  }
                : null
            }
            viewAllButtonText={!loadsOnScroll && page === 'feed' ? i18n('infinite_grid.view_all_markets') : null}
            gridInsert={gridInsert}
          />
        </div>

        {showBetColumn && (
          <aside
            ref={setBetColumn}
            aria-label={i18n('markets.place_bet_column')}
            className="sticky top-[126px] w-[340px] shrink-0 self-start">
            {/* The bet pad portals in here, and so does its loading frame — the selected
                row owns both. Until a row is picked the column says so rather than
                collapsing, which would shift the list sideways. */}
            {!marketSelected && (
              <div className="rounded-lg border border-dashed border-Neutral-3 px-4 py-10 text-center text-sm text-Neutral-4">
                {i18n(isFixtureList ? 'markets.select_match_to_bet' : 'markets.select_market_to_bet')}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* PlaceBet draws the sheet itself — header, close, backdrop, drag-to-dismiss —
          so this is only the node its pad portals into. */}
      {showBetSheet && <div ref={setBetColumn} aria-label={i18n('markets.place_bet_column')} />}
    </React.Fragment>
  )
}

export default MarketListTemplate
